/**
 * Matriz de Risco "Final" (seção 10.3 do modelo de referência, 56 páginas) — usada
 * EXCLUSIVAMENTE pela(s) linha(s) fixa(s) "Fatores Psicossociais relacionados ao
 * trabalho" do Inventário de Riscos (Seção 18). Vocabulário e dimensões completamente
 * diferentes da Matriz de Risco principal (`riskEngine.js`, `MATRIZ_RISCO`, 5x5):
 *
 * - Probabilidade (linhas): Alto, Moderado, Baixo, Inelegível, Muito Baixo (note que
 *   "Crítico" NÃO é um nível de Probabilidade aqui, diferente da matriz principal).
 * - Severidade (colunas): Inelegível, Aceitável, Moderado, Alto, Crítico, Muito Baixo.
 *
 * "Muito Baixo" (ver ATUALIZAÇÃO 2026-09-08 abaixo) NÃO tem mais célula própria nesta
 * matriz: desde 2026-09-10 (Julia Buzzo/PM), quando Probabilidade E Severidade são
 * "Muito Baixo" nos dois eixos (o que inclui ausência total de resposta), o resultado é
 * o caso especial "Aprofundar" nas três colunas, resolvido ANTES da matriz — ver
 * `resolveLinhaPsicossocial`. Nenhuma combinação ASSIMÉTRICA envolvendo "Muito Baixo"
 * (um lado "Muito Baixo", o outro um nível real diferente) foi confirmada.
 *
 * Este arquivo NUNCA deve ser importado por nenhuma linha de risco além de "Fatores
 * Psicossociais..." — as demais linhas (Ruído, Repetitividade, Batidas contra, etc.)
 * continuam resolvendo Nível de Risco exclusivamente via `riskEngine.js`/`MATRIZ_RISCO`.
 * `_helpers/cargoEngine.js` é responsável por essa segregação (chama este módulo apenas
 * ao montar a(s) linha(s) de Fatores Psicossociais).
 */

// Valor literal (case-insensitive) que, quando presente na resposta bruta de
// Probabilidade OU Severidade da linha Psicossocial, dispara o caso especial
// "Aprofundar" — ignorando a matriz por completo.
const APROFUNDAR = 'Aprofundar'

const normalizar = valor =>
  typeof valor === 'string' ? valor.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') : valor

export const isRespostaAprofundar = valorBruto => normalizar(valorBruto) === normalizar(APROFUNDAR)

// Confirmado por Julia Buzzo (PM, 2026-09-10): a opção "Aprofundar" NÃO existe como
// resposta literal selecionável em nenhuma das perguntas 1146 (Probabilidade)/1147
// (Severidade) do Psicossocial — o vocabulário real dessas perguntas é só
// Muito Baixo/Baixo/Inelegível/Aceitável/Moderado/Alto/Crítico (ver
// `PROBABILIDADE_PSICOSSOCIAL`/`SEVERIDADE_PSICOSSOCIAL` abaixo), sem nenhuma opção de
// texto "Aprofundar". A regra de `isRespostaAprofundar` acima veio de uma primeira
// leitura por vídeo, antes desta confirmação — mantida por segurança (nunca lança se um
// dia essa resposta literal existir), mas na prática o gatilho real do "Aprofundar" é
// este: Probabilidade E Severidade respondidas como "Muito Baixo" nos DOIS eixos
// simultaneamente (o que inclui o caso de ausência total de resposta, já que
// `cargoEngine.js#buildLinhaPsicossocial` converte "sem resposta" em "Muito Baixo" nos
// dois lados antes de chamar `resolveLinhaPsicossocial`).
const ehMuitoBaixo = valor => normalizar(valor) === normalizar('Muito Baixo')
const ehMuitoBaixoNosDoisEixos = (probabilidade, severidade) => ehMuitoBaixo(probabilidade) && ehMuitoBaixo(severidade)

// ATUALIZAÇÃO (2026-09-08, Julia Buzzo/PM): print do checklist real mostra que as
// perguntas de Probabilidade (QUESTION_ID 1146) e Severidade (1147) do Psicossocial têm
// vocabulário PRÓPRIO e mais restrito do que a Matriz Final de referência (seção 10.3)
// documentava — "Muito Baixo" é uma opção seletável em AMBAS as perguntas (texto de
// ajuda de cada uma, literal: "Caso o resultado aqui seja 0 (pois não há resultados
// disponíveis ainda), selecione a opção Muito Baixo"), ou seja, "Muito Baixo" já É o
// valor correto de ausência de resultado NESTE vocabulário — nunca o "Irrelevante"/
// "Muito Baixo" do vocabulário da Matriz de Risco PRINCIPAL (`riskEngine.js`, vetores
// diferentes). Adicionado aos dois vetores abaixo só para que `encontrarNivel`
// reconheça o texto (nunca descarte silenciosamente uma resposta real "Muito Baixo"
// como se fosse um nível desconhecido) — ver ABERTO abaixo sobre a matriz em si.
const PROBABILIDADE_PSICOSSOCIAL = ['Alto', 'Moderado', 'Baixo', 'Inelegível', 'Muito Baixo']
const SEVERIDADE_PSICOSSOCIAL = ['Inelegível', 'Aceitável', 'Moderado', 'Alto', 'Crítico', 'Muito Baixo']

// Matriz completa (Probabilidade -> Severidade -> Nível de Risco), transcrita
// integralmente da seção 10.3 "Matriz de Risco Final" do modelo de referência e
// conferida contra a tabela-exemplo menor enviada separadamente pela cliente.
//
// SUPERADO (2026-09-10, Julia Buzzo/PM): a célula "Muito Baixo" x "Muito Baixo" ->
// "Aceitável" (confirmada em 2026-09-08) foi substituída por uma regra nova, que agora
// vale tanto pra Probabilidade+Severidade="Muito Baixo" respondidas quanto pra ausência
// total de resposta: nesses casos o documento deve mostrar Probabilidade="Aprofundar",
// Severidade="Aprofundar" e Nível de Risco="Aprofundar" nas três colunas — SEM passar
// pela matriz (ver `ehMuitoBaixoNosDoisEixos`/`resolveLinhaPsicossocial` abaixo, onde
// esse caso é tratado ANTES de chegar aqui). Por isso a célula "Muito Baixo" foi
// removida desta matriz: ela nunca é mais alcançada em produção (o short-circuit acima
// intercepta o caso antes do lookup), mas manter uma célula morta/inconsistente com o
// comportamento real seria confuso pra quem ler este arquivo depois.
//
// ABERTO que PERMANECE: nenhuma combinação ASSIMÉTRICA envolvendo "Muito Baixo" (ex.:
// Probabilidade="Muito Baixo" x Severidade="Alto", ou vice-versa — um lado "Muito
// Baixo" e o outro um nível real diferente) foi confirmada. `PROBABILIDADE_PSICOSSOCIAL`/
// `SEVERIDADE_PSICOSSOCIAL` continuam reconhecendo "Muito Baixo" como nível válido (pra
// nunca descartar silenciosamente uma resposta real), mas a matriz não tem célula pra
// esses casos assimétricos — `resolvePsicossocialNivelDeRisco` retorna `null` com
// segurança, e o chamador cai no FALLBACK ("N/A") até essas combinações serem
// confirmadas, se um dia forem alcançáveis na prática.
const MATRIZ_PSICOSSOCIAL_FINAL = {
  Alto: { Inelegível: 'Aceitável', Aceitável: 'Moderado', Moderado: 'Alto', Alto: 'Alto', Crítico: 'Crítico' },
  Moderado: { Inelegível: 'Aceitável', Aceitável: 'Aceitável', Moderado: 'Moderado', Alto: 'Alto', Crítico: 'Crítico' },
  Baixo: { Inelegível: 'Inelegível', Aceitável: 'Aceitável', Moderado: 'Moderado', Alto: 'Moderado', Crítico: 'Alto' },
  Inelegível: { Inelegível: 'Inelegível', Aceitável: 'Inelegível', Moderado: 'Aceitável', Alto: 'Moderado', Crítico: 'Moderado' }
}

const encontrarNivel = (lista, valorBruto) => {
  const normalizado = normalizar(valorBruto)
  return lista.find(nivel => normalizar(nivel) === normalizado) || null
}

/**
 * Resolve o Nível de Risco da Matriz Final (seção 10.3) para o par
 * (probabilidade, severidade) já como texto (ex.: "Alto", "Moderado ", "baixo").
 * Retorna null quando um dos dois lados não corresponder a nenhum dos níveis
 * RECONHECIDOS (`PROBABILIDADE_PSICOSSOCIAL`/`SEVERIDADE_PSICOSSOCIAL`) OU quando o
 * nível é reconhecido mas a MATRIZ não tem uma célula para essa combinação (ver ABERTO
 * acima — nenhuma combinação assimétrica envolvendo "Muito Baixo" tem entrada; o caso
 * simétrico "Muito Baixo"/"Muito Baixo" nunca chega aqui, ver `resolveLinhaPsicossocial`)
 * — em ambos os casos nunca lança,
 * nunca inventa uma classificação; o chamador decide o fallback de exibição, mesmo
 * padrão de `riskEngine.js#getNivelRisco`.
 */
export const resolvePsicossocialNivelDeRisco = (probabilidade, severidade) => {
  const probabilidadeResolvida = encontrarNivel(PROBABILIDADE_PSICOSSOCIAL, probabilidade)
  const severidadeResolvida = encontrarNivel(SEVERIDADE_PSICOSSOCIAL, severidade)
  if (!probabilidadeResolvida || !severidadeResolvida) {
    return null
  }
  return MATRIZ_PSICOSSOCIAL_FINAL[probabilidadeResolvida]?.[severidadeResolvida] ?? null
}

/**
 * Resolve a linha completa (Probabilidade/Severidade/Nível de Risco exibidos) para
 * "Fatores Psicossociais relacionados ao trabalho", a partir dos valores BRUTOS (antes
 * de qualquer default de ausência) das respostas de Probabilidade/Severidade.
 *
 * Caso especial "Aprofundar" — REGRA ATUAL (2026-09-10, Julia Buzzo/PM, substitui a
 * regra de 2026-09-08 abaixo): quando Probabilidade E Severidade forem "Muito Baixo" (=
 * 0) nos DOIS eixos — o que inclui o caso de ausência TOTAL de resposta, já convertida
 * em "Muito Baixo"/"Muito Baixo" por `cargoEngine.js#buildLinhaPsicossocial` antes de
 * chamar esta função — a matriz é ignorada por completo e a linha exibe as TRÊS colunas
 * como o texto literal "Aprofundar": Probabilidade = "Aprofundar", Severidade =
 * "Aprofundar", Nível de Risco = "Aprofundar". Isso substitui o resultado anterior
 * (Muito Baixo/Muito Baixo/Aceitável, PR #755, já em produção) para este mesmo caso.
 *
 * HISTÓRICO: a checagem `isRespostaAprofundar` (resposta literal "Aprofundar" numa das
 * duas perguntas) veio de uma primeira leitura por vídeo, ANTES de confirmarmos que o
 * vocabulário real das perguntas 1146/1147 não tem essa opção selecionável (ver
 * `ehMuitoBaixoNosDoisEixos` acima) — mantida por segurança/retrocompatibilidade (nunca
 * lança se essa resposta literal um dia existir), mas o gatilho real na prática é a
 * combinação "Muito Baixo"/"Muito Baixo".
 *
 * CORREÇÃO (2026-09-08, Julia Buzzo/PM, histórico): a regra original desta exceção
 * pedia Probabilidade/Severidade = 0 (número); Julia corrigiu pra usar o TEXTO "Muito
 * Baixo" — mesma opção "Muito Baixo = 0" que já existe nativamente nas perguntas
 * 1146/1147 — nunca o número solto.
 */
export const resolveLinhaPsicossocial = (probabilidadeTexto, severidadeTexto) => {
  if (
    isRespostaAprofundar(probabilidadeTexto) ||
    isRespostaAprofundar(severidadeTexto) ||
    ehMuitoBaixoNosDoisEixos(probabilidadeTexto, severidadeTexto)
  ) {
    return { probabilidadeTexto: APROFUNDAR, severidadeTexto: APROFUNDAR, nivelDeRisco: APROFUNDAR }
  }

  return {
    probabilidadeTexto,
    severidadeTexto,
    nivelDeRisco: resolvePsicossocialNivelDeRisco(probabilidadeTexto, severidadeTexto)
  }
}
